import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CardHeader, Container, Grid, MenuItem, TextField, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";

const minNetSalesOptions = [30000, 40000, 50000, 60000, 70000, 80000, 90000, 100000];
const customerTypeOptions = ["All", "End User", "Dealer"];

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatDate = (value) => {
    if (!value) return "-";

    const parsed = moment(value);
    if (!parsed.isValid()) return value;

    return parsed.format("DD MMM YYYY");
};

const mapCustomerTypeToSegment = (value) => {
    if (value === "End User") return "end_users";
    if (value === "Dealer") return "dealers";
    return "all";
};

export const NetSalesCustomersPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const countryId = Number(state?.countryId) || Number(identity?.user?.country_id);

    const [searchKeyword, setSearchKeyword] = useState("");
    const [customerType, setCustomerType] = useState("All");
    const [minNetSales, setMinNetSales] = useState(30000);
    const searchInputRef = useRef(null);

    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [appliedCustomerType, setAppliedCustomerType] = useState("All");
    const [appliedMinNetSales, setAppliedMinNetSales] = useState(30000);

    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [analysisPeriod, setAnalysisPeriod] = useState({
        fromYear: null,
        toYear: null,
    });
    const [summary, setSummary] = useState({
        totalCount: 0,
        endUsersCount: 0,
        dealersCount: 0,
    });

    const fetchNetSalesCustomers = useCallback(async () => {
        if (!countryId) {
            setData([]);
            setCount(0);
            setSummary({ totalCount: 0, endUsersCount: 0, dealersCount: 0 });
            return;
        }

        setIsLoading(true);
        try {
            const params = {
                min_net_sales: appliedMinNetSales,
                segment: mapCustomerTypeToSegment(appliedCustomerType),
                page: page + 1,
                limit: perPage,
            };

            if (appliedSearchKeyword.trim()) {
                params.search = appliedSearchKeyword.trim();
            }

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${countryId}/net-sales-customers`, {
                params,
            });

            const payload = response?.data?.data || {};
            const rows = (payload.data || []).map((item) => ({
                code: item.customerCode,
                company_name: item.customerName,
                date_created: formatDate(item.dateCreated),
                customer_type: item.customerType,
                last_invoice_date: formatDate(item.lastInvoiceDate),
                net_sales: Number(item.netSales || 0),
            }));

            setData(rows);
            setCount(Number(payload.total || 0));
            setAnalysisPeriod({
                fromYear: payload?.analysisPeriod?.fromYear || null,
                toYear: payload?.analysisPeriod?.toYear || null,
            });

            const summaryPayload = payload?.summary || {};
            setSummary({
                totalCount: Number(summaryPayload.totalCount ?? payload.total ?? 0),
                endUsersCount: Number(summaryPayload.endUsersCount || 0),
                dealersCount: Number(summaryPayload.dealersCount || 0),
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            const message = error?.response?.data?.message || "Unable to fetch net sales customers.";
            Swal.fire({
                icon: "error",
                title: "Failed to Load Data",
                text: message,
                confirmButtonColor: "#fe7320",
            });
        } finally {
            setIsLoading(false);
        }
    }, [countryId, appliedMinNetSales, appliedCustomerType, page, perPage, appliedSearchKeyword, logout]);

    useEffect(() => {
        fetchNetSalesCustomers();
    }, [fetchNetSalesCustomers]);

    const columns = [
        {
            title: "Code",
            field: "code",
            cellStyle: { width: 130, maxWidth: 130 },
            headerStyle: { width: 130, maxWidth: 130 },
        },
        {
            title: "Company Name",
            field: "company_name",
        },
        {
            title: "Date Created",
            field: "date_created",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Customer Type",
            field: "customer_type",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Last Invoice Date",
            field: "last_invoice_date",
            cellStyle: { width: 170, maxWidth: 170 },
            headerStyle: { width: 170, maxWidth: 170 },
        },
        {
            title: "Net Sales",
            field: "net_sales",
            render: (rowData) => formatCurrency(rowData.net_sales),
            cellStyle: { width: 140, maxWidth: 140 },
            headerStyle: { width: 140, maxWidth: 140 },
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title={`Net Sales > ${Math.round(appliedMinNetSales / 1000)}k Customers Analysis`}
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    isLoading={isLoading}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                        rowStyle: () => ({ backgroundColor: "#ffffff" }),
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(nextPerPage) => {
                        setPerPage(nextPerPage);
                        setPage(0);
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <Grid container spacing={2} sx={{ px: 0.1, pb: 1.5 }}>
                                    <Grid item xs={12} sm={8}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    {`Net Sales > ${Math.round(appliedMinNetSales / 1000)}k Customers Analysis`}
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                        <Button color="error" onClick={() => navigate("/organizations")}>
                                            Back
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box
                                    sx={{
                                        mb: 2.0,
                                        border: 1,
                                        borderColor: "#d7e1ed",
                                        borderRadius: 1,
                                        px: 2,
                                        py: 1.3,
                                        backgroundColor: "#ffffff",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 500, color: "#3a4a5a" }}>Overview Summary</Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: "0.9rem", color: "#3a4a5a" }}>
                                        Calculation Formula: Net Sales = Total SI + Total CS + Total DN - Total CN &gt;= {formatCurrency(appliedMinNetSales)}
                                    </Typography>
                                    {analysisPeriod.fromYear && analysisPeriod.toYear && (
                                        <Typography sx={{ mt: 0.5, fontSize: "0.9rem", color: "#3a4a5a" }}>
                                            Selected Analysis Period: <strong>{analysisPeriod.fromYear} to {analysisPeriod.toYear}</strong>
                                        </Typography>
                                    )}
                                </Box>

                                <Typography sx={{ pb: 1.2, color: "#2e3d4f", fontSize: "1rem" }}>
                                    Total Count: {summary.totalCount} Accounts ({summary.endUsersCount} End Users | {summary.dealersCount} Dealers)
                                </Typography>

                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mt: 0.8, pb: 2 }}>
                                    <Box sx={{ flex: "1 1 320px", minWidth: 260, maxWidth: 420 }}>
                                        <TextField
                                            autoComplete="off"
                                            fullWidth
                                            size="small"
                                            label="Search by Name/Code"
                                            autoFocus={searchInputRef.current === document.activeElement}
                                            inputRef={searchInputRef}
                                            value={searchKeyword}
                                            onChange={(event) => setSearchKeyword(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: "0 0 170px", minWidth: 170 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Net Sales"
                                            value={minNetSales}
                                            onChange={(event) => setMinNetSales(Number(event.target.value))}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {minNetSalesOptions.map((value) => (
                                                <MenuItem key={value} value={value}>
                                                    {value.toLocaleString("en-US")}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 220px", minWidth: 220 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Customer Type"
                                            value={customerType}
                                            onChange={(event) => setCustomerType(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {customerTypeOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 130px", minWidth: 130 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            sx={{ height: "40px", whiteSpace: "nowrap" }}
                                            onClick={() => {
                                                setAppliedSearchKeyword(searchKeyword);
                                                setAppliedCustomerType(customerType);
                                                setAppliedMinNetSales(minNetSales);
                                                setPage(0);
                                            }}
                                            disabled={isLoading || !countryId}
                                        >
                                            Show
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        ),
                    }}
                />
            </Container>
        </div>
    );
};
